import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import {
  auth,
  AUTH_ERRORS,
  db,
  storage,
  remoteConfig,
} from "../../api/firebase";
import router from "../../router/index";
import { ROLE, UserData, LoginState } from "../../models/User";
import ErrorService from "@/utils/ErrorService";
import StringHelper from "@/utils/StringHelper";

interface FormData {
  email: string;
  password: string;
}

@Module({ namespaced: true })
export default class Authenication extends VuexModule {
  userData: UserData = {
    userId: "",
    email: "",
    role: ROLE.NONE,
  };

  loginState: LoginState = {
    status: false,
    msg: "",
  };
  allowedRoles = [ROLE.SUPERADMIN, ROLE.ADMIN, ROLE.DEBUG];
  weakPassword = false;
  passwordDialog = false;
  navTab = 0;
  appPrelaunch = 0;

  get getUserData(): UserData {
    return this.userData;
  }
  get isAdmin(): boolean {
    return [ROLE.ADMIN, ROLE.SUPERADMIN].includes(this.userData.role);
  }
  get getNavTab(): number {
    return this.navTab;
  }
  @Mutation
  setAppPreLaunch(value: number): void {
    this.appPrelaunch = value;
  }
  @Mutation
  storeUser(user: UserData): void {
    this.userData = user;
  }
  @Mutation
  setLoginStatus(payload: LoginState): void {
    this.loginState.status = payload.status;
    this.loginState.msg = payload.msg;
  }
  @Mutation
  isWeakPassword(isWeak: boolean): void {
    this.weakPassword = isWeak;
  }
  @Mutation
  showPasswordDialog(passwordDialog: boolean): void {
    this.passwordDialog = passwordDialog;
  }
  @Mutation
  clearUserData(): void {
    this.userData = {
      userId: "",
      email: "",
      role: ROLE.NONE,
    };
    this.loginState = {
      status: false,
      msg: "",
    };
  }
  @Mutation
  setNavTab(tab: number): void {
    this.navTab = tab;
  }

  @Action
  public async getEmailAddress(): Promise<any> {
    db.ref("users")
      .once("value", function(snapshot: { val: () => any }) {
        //console.log(snapshot.val());
      })
      .catch((error) => {
        ErrorService.onError(error);
      });
  }
  @Action
  public async userLoginAsync(formData: FormData): Promise<void> {
    await auth
      .signInWithEmailAndPassword(formData.email, formData.password)
      .then(async (userCred) => {
        // * determine the role
        await auth.currentUser?.getIdTokenResult(true).then((idTokenResult) => {
          if (
            userCred.user &&
            this.allowedRoles.includes(idTokenResult.claims.role)
          ) {
            // * Allow login
            this.context.commit("storeUser", {
              userId: userCred.user.uid,
              email: userCred.user.email,
              role: idTokenResult.claims.role,
            });
            if (!StringHelper.CheckPasswordComplexity(formData.password)) {
              this.context.commit("isWeakPassword", true);
              this.context.commit("showPasswordDialog", true);
            } else {
              this.context.commit("isWeakPassword", false);
              this.context.commit("setLoginStatus", {
                status: true,
                msg: "Granted",
              });
              //Get the remote config values
              //We might push to a single method in future, but at moment there is only 1 value
              remoteConfig
                .fetchAndActivate()
                .then(() => {
                  this.context.commit(
                    "setAppPreLaunch",
                    remoteConfig.getValue("app_prelaunch_period_mins")
                  );
                })
                .catch((err) => {
                  console.log(err);
                });
              this.context.commit(
                "setAppPreLaunch",
                remoteConfig.getValue("app_prelaunch_period_mins")
              );
              // change route to dashboard
              router.push("/Recent");
            }
          } else {
            auth.signOut();
            const ls: LoginState = {
              status: false,
              msg: "Access Denied due to error",
            };
            this.context.commit("setLoginStatus", ls);
          }
        });
      })
      .catch((error) => {
        // Handle Errors here.
        const errorCode = error.code;
        const errorMessage = error.message;
        const ls: LoginState = { status: false, msg: "" };

        if (
          errorCode === AUTH_ERRORS.WRONG_PASS ||
          errorCode === AUTH_ERRORS.NOT_FOUND
        ) {
          ls.msg = "Email/password/user issue";
        } else if (
          errorCode === AUTH_ERRORS.TOO_MANY_REQUESTS ||
          errorCode === AUTH_ERRORS.USER_DISABLED
        ) {
          ls.msg = errorMessage;
        } else {
          ls.msg = "Access Denied due to error";
        }
        this.context.commit("setLoginStatus", ls);
      });
  }
  @Action
  public async userLogoutAsync(): Promise<void> {
    this.context.commit("clearUserData");
    if (!auth || !auth.currentUser) return;

    await auth.signOut().then(() => {
      this.context.commit("setNavTab", 0);
    });
  }

  @Action({ rawError: true })
  public async getImageSrcFromRemote(path: string): Promise<string> {
    return await storage
      .ref(path)
      .getDownloadURL()
      .catch(() => {
        return "Error";
      });
  }

  @Action({ rawError: true })
  public async getImageMetaData(path: string): Promise<any> {
    return await storage
      .ref(path)
      .getMetadata()
      .then((metaData) => {
        return metaData;
      });
  }
  @Action
  public async getAllImageSrcFromRemote(path: string): Promise<any> {
    const listRef = storage.ref(path);
    const firstpage = await listRef.list({ maxResults: 100 });
    return firstpage;
  }

  @Action({ rawError: true })
  public async uploadFile(data: any): Promise<any> {
    if (
      data.file.name.split(".").length - 1 > 1 ||
      data.file.name.split("/").length - 1 > 0
    ) {
      return {
        message:
          "Please make sure the filename does not contain any '.' or '/'",
      };
    } else {
      const listRef = storage.ref(data.path).child(data.file.name);
      return await listRef
        .put(data.file)
        .then((snapshot) => {
          return {
            message: "Success",
            ref: snapshot.ref,
          };
        })
        .catch((error) => {
          return { message: error.message };
        });
    }
  }

  @Action({ rawError: true })
  public async deleteFile(path: string): Promise<string> {
    const listRef = storage.ref().child(path);
    return await listRef
      .delete()
      .then((snapshot) => {
        return "success";
      })
      .catch((error) => {
        return error.message;
      });
  }
  @Action({ rawError: true })
  public async updatePassword(password: string): Promise<string> {
    return await auth.currentUser
      ?.updatePassword(password)
      .then((results) => {
        return "Success";
      })
      .catch((error) => {
        return error;
      });
  }
}
